import React from 'react';
import { Box, Breadcrumbs, Button, Link, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded';

/**
 * PageHeader - the title block every page shares.
 *
 * Renders, top to bottom: an optional back link or breadcrumb trail, then a row
 * with the page title (a real h1 drawn with the h4 variant), an optional lead
 * paragraph under it, and the page's actions on the right (stacked under the
 * title on phones).
 *
 * Props:
 *  - title           : string | node (required)
 *  - lead            : string | node shown under the title
 *  - eyebrow         : short uppercase label above the title
 *  - icon            : node placed before the title text
 *  - titleAdornment  : node placed after the title text (e.g. a status Chip)
 *  - back            : { label?, to?, onClick? } - renders a "Back" text button
 *  - breadcrumbs     : [{ label, to? }] - the last item is the current page
 *  - actions         : node - the page's primary/secondary actions
 *  - sx              : extra styles for the wrapper
 */
const PageHeader = ({
  title,
  lead,
  eyebrow,
  icon,
  titleAdornment,
  back,
  breadcrumbs,
  actions,
  sx,
}) => {
  const theme = useTheme();

  return (
    <Box component="header" sx={{ mb: { xs: 3, sm: 4 }, ...sx }}>
      {back && (
        <Button
          size="small"
          startIcon={<ArrowBackRoundedIcon />}
          {...(back.to ? { component: RouterLink, to: back.to } : {})}
          onClick={back.onClick}
          sx={{ mb: 1.5, ml: -1, color: 'text.secondary' }}
        >
          {back.label || 'Back'}
        </Button>
      )}

      {Array.isArray(breadcrumbs) && breadcrumbs.length > 0 && (
        <Breadcrumbs
          aria-label="Breadcrumb"
          separator={<NavigateNextRoundedIcon fontSize="small" />}
          sx={{ mb: 1.5 }}
        >
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;
            if (isLast || !crumb.to) {
              return (
                <Typography key={`${crumb.label}-${index}`} color="text.primary" variant="body2">
                  {crumb.label}
                </Typography>
              );
            }
            return (
              <Link
                key={`${crumb.label}-${index}`}
                component={RouterLink}
                to={crumb.to}
                underline="hover"
                color="inherit"
                variant="body2"
              >
                {crumb.label}
              </Link>
            );
          })}
        </Breadcrumbs>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box sx={{ minWidth: 0 }}>
          {eyebrow && (
            <Typography
              variant="overline"
              sx={{ display: 'block', color: 'primary.main', letterSpacing: '0.08em', lineHeight: 1.6 }}
            >
              {eyebrow}
            </Typography>
          )}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              ...theme.modern.typography.modernHeading,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            {icon}
            <Box component="span" sx={{ minWidth: 0 }}>{title}</Box>
            {titleAdornment}
          </Typography>
          {lead && (
            <Typography variant="body1" sx={{ ...theme.modern.typography.modernSubtext, mt: 0.75 }}>
              {lead}
            </Typography>
          )}
        </Box>

        {actions && (
          <Stack
            direction="row"
            spacing={1.5}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
            sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}
          >
            {actions}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default PageHeader;
